import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { AuthFrame } from '@/pages/Login';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, readErr } from '@/lib/api';
import GoogleSignInButton from '@/components/GoogleSignInButton';
import CountryCodePicker, { findCountryByIso2 } from '@/components/CountryCodePicker';
import { fetchRegion } from '@/lib/region';
import { toast } from '@/lib/toast';
import { Gift } from 'lucide-react';

// Round 40o — basic phone validation. We don't ship a heavy libphonenumber
// dependency here; just guard against the obvious foot-guns: empty, too
// short, too long, non-numeric characters. Backend re-validates against
// E.164 (`^\+\d{7,15}$`) before persisting.
const PHONE_DIGITS_REGEX = /^\d{7,15}$/;

export default function Signup() {
    const { signup, loading } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });
    const [countryIso2, setCountryIso2] = useState('IN');
    const [inviter, setInviter] = useState(null);   // { referrer_name, reward_credits }

    // Round 40o — pre-select the country code from the visitor's IP region
    // so a US visitor sees +1 by default instead of +91. Resolved async;
    // safe to default to IN until /region/me returns.
    useEffect(() => {
        let alive = true;
        fetchRegion()
            .then((r) => {
                if (alive && r?.country && findCountryByIso2(r.country)) {
                    setCountryIso2(r.country.toUpperCase());
                }
            })
            .catch(() => { /* default IN — fine */ });
        return () => { alive = false; };
    }, []);

    // Round 32 — if a ?ref=CODE was captured into localStorage, validate it
    // against the backend and surface an "Invited by X" banner.
    useEffect(() => {
        const code = (localStorage.getItem('ac_ref') || '').trim();
        if (!code) return;
        api.post('/referral/validate', { code })
            .then((r) => setInviter(r.data))
            .catch(() => {
                // Invalid code — silently drop it so the user can still sign up.
                localStorage.removeItem('ac_ref');
            });
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        // Round 40o — local validation before round-tripping.
        const digits = form.phone.replace(/\D/g, '');
        if (!PHONE_DIGITS_REGEX.test(digits)) {
            toast.error('Please enter a valid phone number (7–15 digits).');
            return;
        }
        const country = findCountryByIso2(countryIso2);
        const phoneE164 = `${country.dial}${digits}`;

        try {
            const result = await signup(
                form.email,
                form.password,
                form.name,
                { phone_e164: phoneE164, phone_country: countryIso2 },
            );
            if (result?.verificationRequired) {
                toast.success('Check your inbox — verification code sent.');
                navigate('/verify', { state: { email: result.email || form.email } });
                return;
            }
            // Legacy path (shouldn't fire post Round 25 but kept defensive).
            toast.success('Welcome — wallet credit added');
            navigate('/onboarding');
        } catch (err) {
            toast.error(readErr(err, 'Signup failed'));
        }
    };

    return (
        <AuthFrame title="Begin the consultation" subtitle="Wallet credit on us — start chatting with Guruji.">
            {inviter && (
                <div
                    className="mb-6 p-3 flex items-start gap-2.5 border border-marigold/30 bg-marigold/5 text-sm"
                    data-testid="invite-banner"
                >
                    <Gift size={16} className="text-marigold mt-0.5 shrink-0" />
                    <div>
                        <strong className="text-marigold">{inviter.referrer_name}</strong>
                        <span className="text-slate-300"> invited you. </span>
                        <span className="text-slate-400">
                            After your first recharge, they'll earn {
                                inviter.reward_label
                                    ? inviter.reward_label
                                    : (inviter.currency === 'USD'
                                        ? `$${Number(inviter.reward_inr || 0).toFixed(2)}`
                                        : `₹${Number(inviter.reward_inr || 0).toLocaleString('en-IN')}`)
                            } wallet credit — thanks for using their link!
                        </span>
                    </div>
                </div>
            )}
            <form onSubmit={onSubmit} className="space-y-5" data-testid="signup-form">
                <GoogleSignInButton label="Continue with Google" testId="signup-google-btn" />
                <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-500">
                    <div className="flex-1 h-px bg-marigold/15" />
                    <span>or with email</span>
                    <div className="flex-1 h-px bg-marigold/15" />
                </div>
                <Field label="Your name" id="name" value={form.name}
                    onChange={(v) => setForm({ ...form, name: v })} autoFocus testId="signup-name" />
                <Field label="Email" id="email" type="email" value={form.email}
                    onChange={(v) => setForm({ ...form, email: v })} testId="signup-email" />

                {/* Round 40o — phone + country code. Combined into one row so
                    the +91 picker visually attaches to the number input. */}
                <div>
                    <Label htmlFor="phone" className="text-slate-300 text-xs uppercase tracking-wider">
                        Mobile number
                    </Label>
                    <div className="mt-2 flex items-center gap-2">
                        <CountryCodePicker
                            value={countryIso2}
                            onChange={(iso2) => setCountryIso2(iso2)}
                            testId="signup-country-code"
                        />
                        <Input
                            id="phone"
                            type="tel"
                            required
                            inputMode="numeric"
                            autoComplete="tel-national"
                            placeholder="98765 43210"
                            value={form.phone}
                            onChange={(e) => setForm({
                                ...form,
                                // Strip non-digits as the user types so what
                                // they see matches what we'll submit. Keep
                                // a small visual gap every 5 digits for
                                // readability without changing the value.
                                phone: e.target.value.replace(/\D/g, ''),
                            })}
                            data-testid="signup-phone"
                            className="flex-1 bg-transparent border-b border-marigold/20 border-t-0 border-l-0 border-r-0 rounded-none px-0 focus-visible:ring-0 focus-visible:border-marigold text-parchment h-11"
                        />
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">
                        We'll only use this to confirm consultations and send important account alerts.
                    </p>
                </div>

                <Field label="Password" id="password" type="password" value={form.password}
                    onChange={(v) => setForm({ ...form, password: v })} testId="signup-password" />
                <Button type="submit" disabled={loading} data-testid="signup-submit"
                    className="w-full bg-marigold text-obsidian hover:bg-marigold-hover rounded-none h-11 font-medium">
                    {loading ? 'Creating…' : 'Create account'}
                </Button>
                <p className="text-sm text-slate-400 text-center">
                    Already here?{' '}
                    <Link to="/login" className="text-marigold hover:text-marigold-50" data-testid="link-login">
                        Sign in
                    </Link>
                </p>
            </form>
        </AuthFrame>
    );
}

function Field({ label, id, type = 'text', value, onChange, autoFocus, testId }) {
    return (
        <div>
            <Label htmlFor={id} className="text-slate-300 text-xs uppercase tracking-wider">{label}</Label>
            <Input
                id={id} type={type} required autoFocus={autoFocus}
                value={value} onChange={(e) => onChange(e.target.value)}
                className="mt-2 bg-transparent border-b border-marigold/20 border-t-0 border-l-0 border-r-0 rounded-none px-0 focus-visible:ring-0 focus-visible:border-marigold text-parchment h-11"
                data-testid={testId}
            />
        </div>
    );
}
